<template>
    <div>
        <Title class="center-title">项目投标申请({{ projectNatureName }})</Title>
        <div class="panel">
            <!-- 第1行：项目名称、项目备案编号 -->
            <div class="label">项目名称</div>
            <div class="value1">{{ extra.projectName || '—' }}</div>
            <div class="label">项目备案编号</div>
            <div class="value2">{{ extra.filingNum || '—' }}</div>

            <!-- 第2行：投标申请编号、填报人 -->
            <div class="label">投标申请编号</div>
            <div class="value1">{{ extra.applyNum || '—' }}</div>
            <div class="label">填报人</div>
            <div class="value2">{{ extra.applyUserName || '—' }}</div>

            <!-- 第3行：主办部门、生产承担部门 -->
            <div class="label">主办部门</div>
            <div class="value1">{{ extra.sponsorDeptName || '—' }}</div>
            <div class="label">生产承担部门</div>
            <div class="value2">{{ extra.projectProductDeptName || '—' }}</div>

            <!-- 第4行：项目性质、用印名称 -->
            <div class="label">项目性质</div>
            <div class="value1">{{ projectNatureName }}</div>
            <div class="label">用印名称</div>
            <div class="value2">
                <DataDictFinder dictType="scrm.scrm_seal_name" :dictValue="extra.sealName" />
            </div>

            <!-- 第5行：主专业、公司专业副总 -->
            <div class="label">主专业</div>
            <div class="value1">
                <DataDictFinder dictType="scrm.scrm_major" :dictValue="extra.mainMajor" />
            </div>
            <div class="label">公司专业副总</div>
            <div class="value2">{{ extra.majorVicePresidentName || '—' }}</div>

            <!-- 第6行：项目地点、业主单位 -->
            <div class="label">项目地点</div>
            <div class="value1">{{ extra.projectAddress || '—' }}</div>
            <div class="label">业主单位</div>
            <div class="value2">{{ extra.customerName || '—' }}</div>

            <!-- 第7行：联系人、联合体情况 -->
            <div class="label">联系人</div>
            <div class="value1">{{ extra.contactPerson || '—' }}</div>
            <div class="label">联合体情况</div>
            <div class="value2">{{ combinationInfoText }}</div>

            <!-- 第8行：联合体协议、联合体单位 -->
            <div class="label">联合体协议</div>
            <div class="value1">
              <template v-if="consortiumAgreementFileList.length > 0">
                <div v-for="item in consortiumAgreementFileList" :key="item.id">
                  <a :href="item.completePath" target="_blank" @click.prevent="handleFileClick($event, item.completePath, item.name || item.fileName)">{{ item.name || item.fileName || item.completePath }}</a>
                </div>
              </template>
              <span v-else>—</span>
            </div>
            <div class="label">联合体单位</div>
            <div class="value2">{{ extra.combinationCompany || '—' }}</div>

            <!-- 第9行：EPC负责人、设计负责人 -->
            <div class="label">EPC负责人</div>
            <div class="value1">{{ extra.epcManagerName || '—' }}</div>
            <div class="label">设计负责人</div>
            <div class="value2">{{ extra.designManagerName || '—' }}</div>

            <!-- 第10行：施工负责人、投标日期 -->
            <div class="label">施工负责人</div>
            <div class="value1">{{ extra.constructionManagerName || '—' }}</div>
            <div class="label">投标日期</div>
            <div class="value2">{{ extra.bidDate | dateFormat }}</div>

            <!-- 第11行：开标方式、报价方式 -->
            <div class="label">开标方式</div>
            <div class="value1">
                <DataDictFinder dictType="scrm.scrm_bid_opening_method" :dictValue="extra.bidOpenMethod" />
            </div>
            <div class="label">报价方式</div>
            <div class="value2">
                <DataDictFinder dictType="scrm.scrm_quotation_method" :dictValue="extra.quoteMethod" />
            </div>

            <!-- 第12行：是否含技术标、总投资(万元) -->
            <div class="label">是否含技术标</div>
            <div class="value1">
                {{ extra.containTechBidFlag === 1 ? '是' : extra.containTechBidFlag === 0 ? '否' : '—' }}
            </div>
            <div class="label">总投资（万元）</div>
            <div class="value2">{{ extra.totalInvestment != null ? extra.totalInvestment : '—' }}</div>

            <!-- 第13行：投标保证金(万元)、项目成本预算描述 -->
            <div class="label">投标保证金（万元）</div>
            <div class="value1">{{ extra.bidSecurity != null ? extra.bidSecurity : '—' }}</div>
            <div class="label">项目成本预算描述</div>
            <div class="value2" style="white-space: pre-wrap">{{ extra.projectCostBudgetDesc || '—' }}</div>

            <!-- 第14行：项目概况、附件 -->
            <div class="label">项目概况</div>
            <div class="value1" style="white-space: pre-wrap">{{ extra.projectSurvey || '—' }}</div>
            <div class="label">附件</div>
            <div class="value2">
                <template v-if="fileList.length > 0">
                    <div v-for="item in fileList" :key="item.id">
                        <a :href="item.completePath" target="_blank" @click.prevent="handleFileClick($event, item.completePath, item.name || item.fileName)">{{ item.name || item.fileName || item.completePath }}</a>
                    </div>
                </template>
                <span v-else>—</span>
            </div>
        </div>
        <Padding />
    </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import Title from "../Title";
import moment from "moment";
import { getProjectFilingDetail } from "@/api/market/projectFiling";

export default {
    components: { Title },
    data() {
        return {
            projectFilingData: {}
        };
    },
    filters: {
        dateFormat(val) {
            if (!val) return '-';
            return moment(val).format('YYYY-MM-DD');
        },
    },
    computed: {
        ...mapState("approval", ["detail"]),
        ...mapGetters("setting", ["findDataDict"]),
        projectNatureName() {
            const dict = this.findDataDict("scrm.scrm_project_nature");
            const item = dict.find(d => String(d.value) === String(this.extra.projectNature));
            return item ? item.name : (this.extra.projectNature || '—');
        },
        extra() {
            const baseExtra = this.detail.extra ? this.detail.extra : {};
            // 合合备案数据到 extra 中
            return {
                ...baseExtra,
                // 如果备案数据中有值，优先使用备案数据
                filingNum: this.projectFilingData.filingNum || baseExtra.filingNum,
                customerName: this.projectFilingData.customerName || baseExtra.customerName,
                projectAddress: this.projectFilingData.projectAddress || baseExtra.projectAddress,
                contactPerson: this.projectFilingData.contactPerson || baseExtra.contactPerson,
            };
        },
        fileList() {
            return this.extra.attachmentFileList || [];
        },
        consortiumAgreementFileList() {
            return this.extra.combinationAgreementFileList || [];
        },
        // combinationInfo: 1=无联合体  2=牵头  3=成员方（与 BidApplyForm 提交值一致）
        combinationInfoText() {
            const v = this.extra.combinationInfo;
            if (v === 1 || v === '1') return '无联合体';
            if (v === 2 || v === '2') return '牵头';
            if (v === 3 || v === '3') return '成员方';
            return '—';
        },
    },
    methods: {
        isPdfFile(filename) {
            return filename && filename.toLowerCase().endsWith('.pdf');
        },
        previewPdf(url, filename) {
            const routeUrl = this.$router.resolve({
                path: '/pdf-preview',
                query: { url, title: filename || 'PDF预览' }
            }).href;
            window.open(routeUrl, '_blank');
        },
        deepDecodeURIComponent(str) {
            // 仅当结果中仍含 %25（编码的百分号）时才继续解码：
            // %25 是"还有一层编码未解"的唯一信号；没有 %25 时再解码会破坏
            // URL 中合法的编码字符（如文件名的 %2B、OSS 签名参数的 %2F），导致下载/查看链接失效
            let decoded = str;
            while (decoded.includes('%25')) {
                const prev = decoded;
                try {
                    decoded = decodeURIComponent(decoded);
                } catch (e) {
                    break;
                }
                if (decoded === prev) break;
            }
            return decoded;
        },
        handleFileClick(event, url, filename) {
            event.preventDefault();
            const decodedUrl = this.deepDecodeURIComponent(url);
            if (this.isPdfFile(filename)) {
                this.previewPdf(decodedUrl, filename);
            } else {
                const a = document.createElement('a');
                a.href = decodedUrl;
                a.download = filename || '';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
        },
        async getProjectFilingData(projectFilingId) {
            try {
                const res = await getProjectFilingDetail(projectFilingId);
                if (res) {
                    // 构建项目地址(省市区)
                    let projectAddress = '';
                    if (res.provinceCode) {
                        projectAddress += res.provinceCode;
                    }
                    if (res.cityCode) {
                        projectAddress += (projectAddress ? ' ' : '') + (res.cityCode );
                    }
                    if (res.regionCode) {
                        projectAddress += (projectAddress ? ' ' : '') + (res.regionCode);
                    }

                    this.projectFilingData = {
                        filingNum: res.filingNum,
                        customerName: res.customerName,
                        projectAddress: projectAddress,
                        contactPerson: res.contactName,
                    };
                }
            } catch (error) {
                console.error('获取项目备案详情失败:', error);
            }
        }
    },
    watch: {
        // 精准监听 projectFilingId：当父组件 store 的 setDetail commit 时，
        // 以及子组件 mounted 首次读取到 store 中的值时，都会在同一个 handler 中统一处理，
        // 避免 mounted + watch detail 两个入口分别触发 getProjectFilingDetail
        'detail.extra.projectFilingId': {
            immediate: true,
            handler(newId) {
                if (newId && newId !== this._lastFilingId) {
                    this._lastFilingId = newId;
                    this.getProjectFilingData(newId);
                }
            }
        }
    }
};
</script>

<style lang="less" scoped>
::v-deep .center-title {
  font-size: 24px;
  left: 0;
  text-align: center;
  margin-top: -65px;
  margin-bottom: 30px;
  margin-left: 150px;

  &::before {
    display: none;
  }
}
.panel {
    display: grid;
    grid-template-columns: 150px 1fr 150px 1fr;
    border-top: 1px solid #e8e8e8;

    & > div {
        padding: 8px;
        border-bottom: 1px solid #e8e8e8;
    }

    .label {
        background-color: #fafafa;
        border-right: 1px solid #e8e8e8;
        border-left: 1px solid #e8e8e8;
        min-width: 150px;
        max-width: 150px;
    }

    .value1 {
        grid-column-start: 2;
        grid-column-end: 3;
    }

    .value2 {
        grid-column-start: 4;
        grid-column-end: 5;
        border-right: 1px solid #e8e8e8;
    }
}
</style>
